import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FORM_CONTROLS, TYPOGRAPHY, SPACING, TABS } from "@/design-system";
import { DELAYS } from '@/constants/timing';
import { FAQAccordionSkeleton } from "@/components/skeletons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { useLocalizations } from "@/hooks/useLocalizations";
import { LPages, LCommon } from "@/components/i18n";
import { SEOHead, generateFAQPageSchema, generateWebSiteSchema } from "@/components/seo/SEOHead";
import { DEFAULT_PAGE_AUTHOR, getFullTitle } from "@/config/seo";
import { useAnalyticsTracking } from "@/hooks/useAnalyticsTracking";
import { useLocale } from "@/contexts/LocaleContext";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";

import { useFAQs } from "@/hooks/useFAQs";
import { FAQSearchAutocomplete } from "@/components/FAQSearchAutocomplete";
import { FAQFeedbackButtons } from "@/components/FAQFeedbackButtons";
import { FAQActionDialog, FAQActionType } from "@/components/FAQActionDialog";
import { lazy, Suspense } from "react";
const HelpCenterFloatingChat = lazy(() => import("@/components/HelpCenterFloatingChat").then(m => ({ default: m.HelpCenterFloatingChat })));
import { TicketIcon, Loader2, Phone, Key, Mail, Wand2 } from "lucide-react";
import { useDebouncedCallback } from "use-debounce";
import { RESPONSIVE } from "@/design-system";
import { cn } from "@/lib/utils";
import { SUPABASE_PROJECT_ID } from '@/config/supabase';
import { DOMAIN_DEFAULTS } from '@/lib/domainDefaults';

// Contact form validation schema
type TranslationFn = (key: string, options?: { defaultValue?: string }) => string;

// 13 categories matching the backend CHECK constraint
// (help_center_tickets_submitted_category_check from PR-1).
const SUBMITTED_CATEGORIES = [
  'payment_not_recognized',
  'subscription_lapsed_unexpectedly',
  'cancel_subscription',
  'change_payment_method',
  'login_or_password',
  'email_verification',
  'change_account_email',
  'invoice_or_nf_request',
  'content_access',
  'profile_or_expert_listing',
  'pre_sales_question',
  'bug_or_unexpected_behavior',
  'other',
] as const;

const createContactSchema = (t: TranslationFn) => z.object({
  name: z.string()
    .trim()
    .min(1, { message: t('validation.name_required', { defaultValue: 'Name is required' }) })
    .max(100, { message: t('validation.name_max_length', { defaultValue: 'Name is too long' }) }),
  email: z.string()
    .trim()
    .email({ message: t('validation.invalid_email', { defaultValue: 'Invalid email address' }) })
    .max(255, { message: t('validation.email_max_length', { defaultValue: 'Email is too long' }) }),
  submittedCategory: z.enum(SUBMITTED_CATEGORIES, {
    errorMap: () => ({
      message: t('validation.category_required', { defaultValue: 'Please select a category' }),
    }),
  }),
  contextWhatTried: z.string()
    .trim()
    .min(1, { message: t('validation.context_what_tried_required', { defaultValue: 'Please describe what you were trying to do' }) })
    .max(1000, { message: t('validation.context_what_tried_max_length', { defaultValue: 'Please keep this under 1000 characters' }) }),
  contextWhatHappened: z.string()
    .trim()
    .min(1, { message: t('validation.context_what_happened_required', { defaultValue: 'Please describe what happened' }) })
    .max(1000, { message: t('validation.context_what_happened_max_length', { defaultValue: 'Please keep this under 1000 characters' }) }),
  message: z.string()
    .trim()
    .min(10, { message: t('validation.message_min_length', { defaultValue: 'Message must be at least 10 characters' }) })
    .max(2000, { message: t('validation.message_max_length', { defaultValue: 'Message is too long' }) }),
});

type ContactFormData = z.infer<ReturnType<typeof createContactSchema>>;

// Category options with i18n keys + EN defaults. value strings are the
// stable DB-side enum keys; display strings replaceable via i18n.
const CATEGORY_OPTIONS = [
  { value: 'payment_not_recognized',
    labelKey: 'help_center.contact_form.categories.payment_not_recognized',
    labelDefault: 'I paid but I do not have access' },
  { value: 'subscription_lapsed_unexpectedly',
    labelKey: 'help_center.contact_form.categories.subscription_lapsed_unexpectedly',
    labelDefault: 'My subscription lapsed unexpectedly' },
  { value: 'cancel_subscription',
    labelKey: 'help_center.contact_form.categories.cancel_subscription',
    labelDefault: 'I want to cancel my subscription' },
  { value: 'change_payment_method',
    labelKey: 'help_center.contact_form.categories.change_payment_method',
    labelDefault: 'Change my payment method' },
  { value: 'login_or_password',
    labelKey: 'help_center.contact_form.categories.login_or_password',
    labelDefault: 'Login or password problem' },
  { value: 'email_verification',
    labelKey: 'help_center.contact_form.categories.email_verification',
    labelDefault: 'Email verification issue' },
  { value: 'change_account_email',
    labelKey: 'help_center.contact_form.categories.change_account_email',
    labelDefault: 'Change my account email' },
  { value: 'invoice_or_nf_request',
    labelKey: 'help_center.contact_form.categories.invoice_or_nf_request',
    labelDefault: 'Invoice or NF question' },
  { value: 'content_access',
    labelKey: 'help_center.contact_form.categories.content_access',
    labelDefault: 'I cannot access content I paid for' },
  { value: 'profile_or_expert_listing',
    labelKey: 'help_center.contact_form.categories.profile_or_expert_listing',
    labelDefault: 'Profile or expert listing question' },
  { value: 'pre_sales_question',
    labelKey: 'help_center.contact_form.categories.pre_sales_question',
    labelDefault: 'Pre-sales question (not yet a customer)' },
  { value: 'bug_or_unexpected_behavior',
    labelKey: 'help_center.contact_form.categories.bug_or_unexpected_behavior',
    labelDefault: 'I found a bug or unexpected behavior' },
  { value: 'other',
    labelKey: 'help_center.contact_form.categories.other',
    labelDefault: 'Other' },
];

const HelpCenter = () => {
  const { user } = useAuth();
  const { lPages, lCommon } = useLocalizations();
  const { trackEvent, trackFormSubmit, trackSearchSubmit } = useAnalyticsTracking();
  const { settings } = useLocale();
  // PR-2j: forwarded to submit-help-center-ticket so the customer
  // confirmation email renders in the language the customer is
  // currently browsing in (matches the rest of the app's i18n usage).
  const currentLocale = useCurrentLocale();

  const contactSchema = createContactSchema(lCommon);
  const accordionRef = useRef<HTMLDivElement>(null);
  
  // FAQ search state
  const [searchQuery, setSearchQuery] = useState("");
  const [openAccordionItem, setOpenAccordionItem] = useState<string | undefined>();
  
  // FAQ action dialog state
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    type: FAQActionType | null;
  }>({ open: false, type: null });
  
  // Contact form state. submittedCategory is intentionally an empty
  // string here (not a valid enum value) so the progressive-disclosure
  // UX can detect "no category yet" before zod validation runs at submit.
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    submittedCategory: "" as ContactFormData['submittedCategory'],
    contextWhatTried: "",
    contextWhatHappened: "",
    message: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Server-issued unix_ms reference for the success screen; null = no
  // submission this session, so render the form instead.
  const [submittedReference, setSubmittedReference] = useState<number | null>(null);

  // Fetch FAQs from database
  const { data: dbFaqs = [], isLoading: faqsLoading } = useFAQs({ 
    limit: 20,
  });

  // Fetch user profile data if logged in
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('users')
        .select('name, display_name, email')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Prefill forms when user data is available
  useEffect(() => {
    if (userProfile) {
      setFormData(prev => ({
        ...prev,
        name: userProfile.display_name || userProfile.name || '',
        email: userProfile.email || user?.email || '',
      }));
    } else if (user?.email) {
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
      }));
    }
  }, [userProfile, user]);

  // Filter FAQs by search query
  const filteredFaqs = useMemo(() => {
    if (!searchQuery.trim()) return dbFaqs;
    const query = searchQuery.toLowerCase();
    return dbFaqs.filter(faq => 
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query) ||
      faq.keywords?.some(k => k.toLowerCase().includes(query))
    );
  }, [dbFaqs, searchQuery]);

  // Debounced search tracking
  const debouncedTrackSearch = useDebouncedCallback(
    (query: string, resultsCount: number) => {
      if (query.trim().length >= 2) {
        trackSearchSubmit(query.trim().toLowerCase(), resultsCount, { source: 'help_center' });
      }
    },
    1000
  );

  // Track searches when query or results change
  useEffect(() => {
    if (searchQuery.trim()) {
      debouncedTrackSearch(searchQuery, filteredFaqs.length);
    }
  }, [searchQuery, filteredFaqs.length, debouncedTrackSearch]);

  // Convert FAQs for schema
  const faqsForSchema = useMemo(() => 
    dbFaqs.map(f => ({ question: f.question, answer: f.answer })),
    [dbFaqs]
  );

  // Track FAQ click
  const handleFaqClick = useCallback((faqId: string, question: string) => {
    trackEvent({
      eventType: 'faq_click',
      eventCategory: 'content',
      metadata: { faqId, question: question.substring(0, 100) },
      sourcePage: '/help-center'
    });
  }, [trackEvent]);

  // Handle autocomplete selection - scroll to and expand FAQ
  const handleFaqSelect = useCallback((faqId: string) => {
    setOpenAccordionItem(`faq-${faqId}`);
    // Scroll to accordion after a short delay
    setTimeout(() => {
      const element = document.getElementById(`faq-item-${faqId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, DELAYS.ANIMATION_FAST);
    
    const faq = dbFaqs.find(f => f.id === faqId);
    if (faq) {
      handleFaqClick(faqId, faq.question);
    }
  }, [dbFaqs, handleFaqClick]);

  // Handle FAQ feedback
  const handleFaqFeedback = useCallback((faqId: string, helpful: boolean) => {
    trackEvent({
      eventType: helpful ? 'help_faq_helpful' : 'help_faq_not_helpful',
      eventCategory: 'engagement',
      metadata: { faqId },
      sourcePage: '/help-center'
    });
  }, [trackEvent]);


  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof ContactFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setIsSubmitting(true);

    try {
      const validatedData = contactSchema.parse(formData);

      const { data, error } = await supabase.functions.invoke('submit-help-center-ticket', {
        body: {
          name: validatedData.name,
          email: validatedData.email,
          submittedCategory: validatedData.submittedCategory,
          contextWhatTried: validatedData.contextWhatTried,
          contextWhatHappened: validatedData.contextWhatHappened,
          message: validatedData.message,
          sourceDomain: window.location.hostname,
          locale: currentLocale,
        },
      });

      if (error) throw error;

      // The edge function wraps its body as { success: true, data: {...} };
      // supabase.functions.invoke returns the unwrapped body in `data`,
      // so the ticket payload lives at `data.data`.
      const payload = (data as { data?: { ticketId?: string; subject?: string; unixMs?: number } })?.data;
      if (!payload?.unixMs) {
        throw new Error('Submission succeeded but reference is missing');
      }

      setSubmittedReference(payload.unixMs);

      trackFormSubmit('help_center');

      toast.success(
        DOMAIN_DEFAULTS.isTributario
          ? 'Mensagem enviada com sucesso'
          : 'Message sent successfully',
        {
          description: DOMAIN_DEFAULTS.isTributario
            ? 'Responderemos em até 24 horas.'
            : 'We will reply within 24 hours.',
        }
      );

      // Reset content fields; preserve name/email if logged in (existing pattern).
      if (!user) {
        setFormData({
          name: "",
          email: "",
          submittedCategory: "" as ContactFormData['submittedCategory'],
          contextWhatTried: "",
          contextWhatHappened: "",
          message: "",
        });
      } else {
        setFormData(prev => ({
          ...prev,
          submittedCategory: "" as ContactFormData['submittedCategory'],
          contextWhatTried: "",
          contextWhatHappened: "",
          message: "",
        }));
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof ContactFormData, string>> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as keyof ContactFormData] = err.message;
          }
        });
        setErrors(fieldErrors);
        toast.error(lCommon('toast.fix_form_errors.error', { defaultValue: 'Please fix form errors.' }));
      } else {
        toast.error(lCommon('toast.failed_to_send.error', { defaultValue: 'Failed to send. Please try again.' }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqSchema = useMemo(() => generateFAQPageSchema(faqsForSchema), [faqsForSchema]);

  return (
    <>
      <SEOHead
        title={getFullTitle(lPages('help_center.title', { defaultValue: 'Help Center' }))}
        description={lPages('help_center.subtitle', { defaultValue: 'Explore articles and contact support' })}
        author={DEFAULT_PAGE_AUTHOR}
        type="website"
        schema={[generateWebSiteSchema(), faqSchema]}
        locale={(settings.locale || settings.language).replace('-', '_')}
      />
      <main className={cn("container", RESPONSIVE.pageContainerPy)}>
        <PageHeader
          title={<LPages id="help_center.title" defaultValue="Help Center" />}
          subtitle={<LPages id="help_center.subtitle" defaultValue='Explore articles and contact support' />}
          centered
        />

        {/* FAQ Search Section with Autocomplete */}
        <section className="mb-12">
          <div className="max-w-2xl mx-auto">
            <FAQSearchAutocomplete
              faqs={dbFaqs}
              value={searchQuery}
              onChange={setSearchQuery}
              onSelect={handleFaqSelect}
              placeholder={lPages('help_center.search.placeholder', { defaultValue: 'Search...' })}
            />
          </div>
        </section>

        {/* Database-driven FAQs Section */}
        <section className="mb-16">
          <h2 className={cn(TYPOGRAPHY.metricValue, SPACING.marginBottom6, 'text-center')}>
            <LPages id="help_center.faqs.title" defaultValue='FAQs' />
          </h2>
          <div className="max-w-3xl mx-auto" ref={accordionRef}>
            {faqsLoading ? (
              <FAQAccordionSkeleton count={5} />
            ) : filteredFaqs.length === 0 && searchQuery.trim() ? (
              <div className="text-center py-8 text-muted-foreground">
                <LPages id="help_center.search.no_results" defaultValue='No results found.' />
              </div>
            ) : (
              <Accordion 
                type="single" 
                collapsible 
                className={cn(SPACING.stackLg)}
                value={openAccordionItem}
                onValueChange={setOpenAccordionItem}
              >
                {/* Database FAQs */}
                {filteredFaqs.map((faq) => (
                  <AccordionItem 
                    key={faq.id}
                    id={`faq-item-${faq.id}`}
                    value={`faq-${faq.id}`} 
                    className="border rounded-lg px-6 bg-card"
                  >
                    <AccordionTrigger 
                      className={cn(TYPOGRAPHY.sectionTitle, 'hover:no-underline py-4')}
                      onClick={() => handleFaqClick(faq.id, faq.localizedQuestion || faq.question)}
                    >
                      {faq.localizedQuestion || faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4">
                      <p className="whitespace-pre-wrap">{faq.localizedAnswer || faq.answer}</p>
                      
                      {/* Action buttons based on FAQ action type */}
                      {faq.action === 'password_reset' && (
                        <Button 
                          onClick={() => setActionDialog({ open: true, type: 'password_reset' })}
                          className={cn(TABS.contentNested)}
                          variant="outline"
                        >
                          <Key className="mr-2 h-4 w-4" />
                          <LCommon id="auth.password_reset.button" defaultValue='Reset Password' />
                        </Button>
                      )}
                      
                      {faq.action === 'resend_verification' && (
                        <Button 
                          onClick={() => setActionDialog({ open: true, type: 'resend_verification' })}
                          className={cn(TABS.contentNested)}
                          variant="outline"
                        >
                          <Mail className="mr-2 h-4 w-4" />
                          <LCommon id="auth.resend_verification.button" defaultValue='Resend Verification Email' />
                        </Button>
                      )}
                      
                      {faq.action === 'magic_link' && (
                        <Button 
                          onClick={() => setActionDialog({ open: true, type: 'magic_link' })}
                          className={cn(TABS.contentNested)}
                          variant="outline"
                        >
                          <Wand2 className="mr-2 h-4 w-4" />
                          <LCommon id="auth.magic_link.button" defaultValue='Sign In with Magic Link' />
                        </Button>
                      )}
                      
                      <FAQFeedbackButtons
                        faqId={faq.id}
                        helpfulCount={faq.helpful_count}
                        notHelpfulCount={faq.not_helpful_count}
                        onFeedback={(helpful) => handleFaqFeedback(faq.id, helpful)}
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}

                {/* Contact Us - Always Last with Embedded Form */}
                <AccordionItem 
                  value="contact-us" 
                  className="border rounded-lg px-6 bg-card border-primary/20"
                >
                  <AccordionTrigger className={cn(TYPOGRAPHY.sectionTitle, 'hover:no-underline py-4')}>
                    <div className={cn(RESPONSIVE.flexRowCenterGap2)}>
                      <Phone className="h-5 w-5 text-primary" />
                      <span><LPages id="help_center.contact.title" defaultValue='Contact Us' /></span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    {submittedReference !== null ? (
                      <div className={cn(SPACING.contentContainer, 'text-center py-4')}>
                        <p className={cn(TYPOGRAPHY.sectionTitle, SPACING.marginBottom4)}>
                          <LPages id="help_center.contact_form.success.title" defaultValue="Your support request has been received" />
                        </p>
                        <p className={cn('text-muted-foreground', SPACING.marginBottom2)}>
                          <LPages id="help_center.contact_form.success.reference_label" defaultValue="Your reference number is" />
                        </p>
                        <p className={cn(TYPOGRAPHY.metricValue, 'font-mono')}>
                          {submittedReference}
                        </p>
                        <p className={cn('text-muted-foreground text-sm', SPACING.marginTop4)}>
                          <LPages id="help_center.contact_form.success.description" defaultValue="We'll respond within 24 hours. Please quote this reference number in any follow-up." />
                        </p>
                        <Button
                          variant="outline"
                          className={cn(SPACING.marginTop4)}
                          onClick={() => setSubmittedReference(null)}
                        >
                          <LPages id="help_center.contact_form.success.new_request_cta" defaultValue="Submit another request" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <p className={cn(SPACING.sectionHeaderLarge, 'text-muted-foreground')}>
                          <LPages id="help_center.contact.description" defaultValue='If you cannot find the answer to your question, please contact us directly.' />
                          <LPages id="help_center.contact.send_message_cta" defaultValue="Send us a message and we'll get back to you within 24 hours." />
                        </p>

                        <form onSubmit={handleSubmit} className={cn(SPACING.contentContainer)}>
                          <div className={cn(SPACING.formFieldGap)}>
                            <Label htmlFor="name"><LPages id="help_center.contact_form.name.label" defaultValue='Name' /></Label>
                            <Input
                              id="name"
                              name="name"
                              placeholder={lPages('help_center.contact_form.name.placeholder', { defaultValue: 'Enter name' })}
                              value={formData.name}
                              onChange={handleInputChange}
                              className={errors.name ? "border-destructive" : ""}
                              disabled={isSubmitting}
                            />
                            {errors.name && (
                              <p className="text-sm text-destructive">{errors.name}</p>
                            )}
                          </div>

                          <div className={cn(SPACING.formFieldGap)}>
                            <Label htmlFor="email"><LPages id="help_center.contact_form.email.label" defaultValue='Email Address' /></Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              placeholder={lPages('help_center.contact_form.email.placeholder', { defaultValue: 'Enter email' })}
                              value={formData.email}
                              onChange={handleInputChange}
                              className={errors.email ? FORM_CONTROLS.validation.errorBorder : ""}
                              disabled={isSubmitting}
                            />
                            {errors.email && (
                              <p className="text-sm text-destructive">{errors.email}</p>
                            )}
                          </div>

                          <div className={cn(SPACING.formFieldGap)}>
                            <Label htmlFor="submittedCategory"><LPages id="help_center.contact_form.category.label" defaultValue='Category' /></Label>
                            <Select
                              value={formData.submittedCategory}
                              onValueChange={(value) => setFormData(prev => ({ ...prev, submittedCategory: value as ContactFormData['submittedCategory'] }))}
                              disabled={isSubmitting}
                            >
                              <SelectTrigger
                                id="submittedCategory"
                                className={cn(
                                  FORM_CONTROLS.select.trigger.full,
                                  errors.submittedCategory && FORM_CONTROLS.validation.errorBorder
                                )}
                              >
                                <SelectValue placeholder={lPages('help_center.contact_form.category.placeholder', { defaultValue: 'Select category' })} />
                              </SelectTrigger>
                              <SelectContent>
                                {CATEGORY_OPTIONS.map(option => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {lPages(option.labelKey, { defaultValue: option.labelDefault })}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {errors.submittedCategory && (
                              <p className="text-sm text-destructive">{errors.submittedCategory}</p>
                            )}
                          </div>

                          {/* Progressive disclosure: the diagnostic context, message,
                              and submit button only mount once the customer commits
                              to a category. Keeps the form light until then. */}
                          {(formData.submittedCategory as string) !== '' && (
                            <>
                              <div className={cn(SPACING.formFieldGap)}>
                                <Label htmlFor="contextWhatTried"><LPages id="help_center.contact_form.context_what_tried.label" defaultValue="What were you trying to do?" /></Label>
                                <Input
                                  id="contextWhatTried"
                                  name="contextWhatTried"
                                  placeholder={lPages('help_center.contact_form.context_what_tried.placeholder', { defaultValue: 'e.g., Log into my account, complete a purchase, etc.' })}
                                  value={formData.contextWhatTried}
                                  onChange={handleInputChange}
                                  className={errors.contextWhatTried ? FORM_CONTROLS.validation.errorBorder : ""}
                                  disabled={isSubmitting}
                                />
                                {errors.contextWhatTried && (
                                  <p className="text-sm text-destructive">{errors.contextWhatTried}</p>
                                )}
                              </div>

                              <div className={cn(SPACING.formFieldGap)}>
                                <Label htmlFor="contextWhatHappened"><LPages id="help_center.contact_form.context_what_happened.label" defaultValue="What happened?" /></Label>
                                <Input
                                  id="contextWhatHappened"
                                  name="contextWhatHappened"
                                  placeholder={lPages('help_center.contact_form.context_what_happened.placeholder', { defaultValue: 'e.g., Got an error message, page would not load, etc.' })}
                                  value={formData.contextWhatHappened}
                                  onChange={handleInputChange}
                                  className={errors.contextWhatHappened ? FORM_CONTROLS.validation.errorBorder : ""}
                                  disabled={isSubmitting}
                                />
                                {errors.contextWhatHappened && (
                                  <p className="text-sm text-destructive">{errors.contextWhatHappened}</p>
                                )}
                              </div>

                              <div className={cn(SPACING.formFieldGap)}>
                                <Label htmlFor="message"><LPages id="help_center.contact_form.message" defaultValue='Please describe your issue or question in detail.' /></Label>
                                <Textarea
                                  id="message"
                                  name="message"
                                  placeholder={lPages('help_center.contact_form.message', { defaultValue: 'Please describe your issue or question in detail.' })}
                                  rows={5}
                                  value={formData.message}
                                  onChange={handleInputChange}
                                  className={errors.message ? FORM_CONTROLS.validation.errorBorder : ""}
                                  disabled={isSubmitting}
                                />
                                {errors.message && (
                                  <p className="text-sm text-destructive">{errors.message}</p>
                                )}
                              </div>

                              <Button type="submit" className="w-full" disabled={isSubmitting}>
                                {isSubmitting ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    <LCommon id="actions.sending" defaultValue='Sending' />
                                  </>
                                ) : (
                                  <>
                                    <Mail className="mr-2 h-4 w-4" />
                                    <LPages id="help_center.contact_form.submit" defaultValue='Submit' />
                                  </>
                                )}
                              </Button>
                            </>
                          )}
                        </form>
                      </>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </div>
        </section>
      </main>
      {/* Floating Chat Widget */}
      <Suspense fallback={null}>
        <HelpCenterFloatingChat />
      </Suspense>
      {/* FAQ Action Dialog */}
      <FAQActionDialog
        open={actionDialog.open}
        onOpenChange={(open) => setActionDialog({ open, type: open ? actionDialog.type : null })}
        actionType={actionDialog.type}
        defaultEmail={user?.email || formData.email}
      />
    </>
  );
};

export default HelpCenter;
